import type { SegmentDTO } from '@next-space/fe-api-idl';
import type { IContent, ISelection } from '@next-space/fe-inlined';
import React, { useMemo, useRef } from 'react';
import { getOrderListNumber } from 'src/editor/editor/plugin/order-list-helper';
import { segmentsToText } from 'src/editor/utils/editor';
import { useThrottleUpdateSegments } from 'src/hooks/block/use-throttle-update-block';
import { useObservableStore } from 'src/services/rxjs-redux/hook';
import { usePickBlock } from 'src/utils/pick-block';
import { BlockSymbol } from '../component/block-symbol-container';
import { NodeWrapper } from '../component/node-wrapper';
import { useLastWidthBeforeEditing } from '../hook/use-last-width-before-editing';
import { useMindNodePadding } from '../hook/use-mind-node-style';
import { getDefaultPlaceHolder } from '../utils/mind-node-util';
import type { MindNodeElement } from './all-node-renderer';
import { MindMapRichText } from './editor/mind-map-rich-text';

export const OrderListNode: MindNodeElement = React.memo((props) => {
  const block = usePickBlock(props.id, ['data'], ['segments']);
  const placeHolder = getDefaultPlaceHolder(props.level);
  const updateSegments = useThrottleUpdateSegments(props.id);

  const isEmpty = useMemo(() => {
    return !segmentsToText(block?.data.segments);
  }, [block?.data.segments]);

  const containerRef = useRef<HTMLDivElement>(null);
  const lastWidth = useLastWidthBeforeEditing(props.id, containerRef, isEmpty, props.level);
  const { paddingRight } = useMindNodePadding(props.id, props.level);
  const listNo = useObservableStore(
    (state) => {
      const { blocks } = state;
      return getOrderListNumber(props.id, blocks);
    },
    [props.id]
  );

  if (!block) return null;

  return (
    <NodeWrapper
      id={props.id}
      ref={containerRef}
      className="flex items-center"
      level={props.level}
      style={{
        paddingLeft: `12px`,
        minWidth: lastWidth,
      }}
    >
      <BlockSymbol
        symbolClassName="mt-0"
        symbol={<div className="text-center inline-block dot mr-1.5">{listNo}</div>}
        editorNode={
          <MindMapRichText
            placeholder={placeHolder}
            uuid={props.id}
            className="whitespace-pre-wrap break-words"
            segments={block.data.segments}
            onChange={(
              segments: SegmentDTO[],
              prevContent: IContent,
              prevSelection: ISelection | null
            ) => {
              updateSegments(segments, [prevContent, prevSelection]);
            }}
            style={{
              paddingRight: `${paddingRight}px`,
            }}
          />
        }
      />
    </NodeWrapper>
  );
});
